import type { AnamnesisSections } from '../../../utils/clinicalDocuments';
import type { AssessmentResultV2 } from '../types';
import { normalizeAssessmentReportSourceSnapshot, type AssessmentReportSourceSnapshotV2 } from '../reports/assessmentReportDomain';

export const INITIAL_ASSESSMENT_SCHEMA_VERSION = 1 as const;

export interface InitialAssessmentAnamnesisSnapshot {
  sourceRecordId: string;
  sourceRecordDate: string;
  sourceRecordTitle?: string;
  sourceKind?: 'structured_anamnesis' | 'initial_anamnesis' | 'clinical_history_anamnesis';
  documentVersion: number;
  sections: AnamnesisSections;
}

export type InitialAssessmentMoment = 'initial' | 'reassessment' | 'final';
export type PhysicalExaminationStatus = 'normal' | 'altered' | 'not_examined' | 'not_applicable';
export type MeasurableGoalStatus = 'planned' | 'achieved' | 'partial' | 'not_achieved';

export interface InitialAssessmentPhysicalFinding {
  id: string;
  domain: string;
  label: string;
  status: PhysicalExaminationStatus;
  finding: string;
}

export interface InitialAssessmentMeasurableGoal {
  id: string;
  activity: string;
  baseline: string;
  target: string;
  deadline: string;
  status: MeasurableGoalStatus;
}

export const INITIAL_ASSESSMENT_MOMENT_LABELS: Record<InitialAssessmentMoment, string> = {
  initial: 'Avaliação inicial',
  reassessment: 'Reavaliação',
  final: 'Avaliação final / alta',
};

export const PHYSICAL_EXAMINATION_STATUS_LABELS: Record<PhysicalExaminationStatus, string> = {
  normal: 'Sem alteração',
  altered: 'Alterado',
  not_examined: 'Não examinado',
  not_applicable: 'Não aplicável',
};

export const MEASURABLE_GOAL_STATUS_LABELS: Record<MeasurableGoalStatus, string> = {
  planned: 'Planejada',
  achieved: 'Alcançada',
  partial: 'Parcialmente alcançada',
  not_achieved: 'Não alcançada',
};

export interface InitialAssessmentExternalExamSnapshot {
  recordId: string;
  title: string;
  recordDate: string;
  note: string;
  sourceUpdatedAt?: string | null;
}

export interface InitialAssessmentExtendedFields {
  assessmentMoment: InitialAssessmentMoment;
  episodeLabel: string;
  baselineResultId: string | null;
  previousFunction: string;
  physicalExamination: InitialAssessmentPhysicalFinding[];
  measurableGoals: InitialAssessmentMeasurableGoal[];
  prognosis: string;
  followUpPlan: string;
  closureReason: string;
  selectedResults: AssessmentReportSourceSnapshotV2[];
  externalExams: InitialAssessmentExternalExamSnapshot[];
}

export interface InitialAssessmentResultPayload extends Partial<InitialAssessmentExtendedFields> {
  schemaVersion: typeof INITIAL_ASSESSMENT_SCHEMA_VERSION;
  assessmentReason: string;
  referralDiagnosis: string;
  currentClinicalCondition: string;
  mainSymptoms: string;
  functionalLimitations: string;
  assistiveDevices: string;
  precautions: string;
  patientGoals: string;
  professionalSynthesis: string;
  initialPlan: string;
  additionalNotes: string;
  anamnesisSnapshot: InitialAssessmentAnamnesisSnapshot | null;
  finishedAt: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
}

export type NormalizedInitialAssessmentPayload = InitialAssessmentResultPayload & InitialAssessmentExtendedFields;

export interface InitialAssessmentSummary {
  statusLabel: string;
  classification: 'Registro clínico descritivo';
  filledSections: number;
  hasAnamnesisSnapshot: boolean;
  assessmentReason: string;
  professionalSynthesis: string;
}

export const INITIAL_ASSESSMENT_FIELD_LABELS: ReadonlyArray<{
  key: Exclude<
    keyof InitialAssessmentResultPayload,
    'schemaVersion' | 'anamnesisSnapshot' | 'finishedAt' | 'patientContext' | 'professionalContext' |
    'assessmentMoment' | 'episodeLabel' | 'baselineResultId' | 'physicalExamination' | 'measurableGoals' | 'selectedResults' | 'externalExams'
  >;
  label: string;
}> = [
  { key: 'assessmentReason', label: 'Motivo da avaliação' },
  { key: 'referralDiagnosis', label: 'Encaminhamento ou diagnóstico informado' },
  { key: 'currentClinicalCondition', label: 'Condição clínica atual' },
  { key: 'previousFunction', label: 'Funcionalidade anterior' },
  { key: 'mainSymptoms', label: 'Sintomas e queixas atuais' },
  { key: 'functionalLimitations', label: 'Limitações funcionais e de participação' },
  { key: 'assistiveDevices', label: 'Dispositivos, órteses e suporte em uso' },
  { key: 'precautions', label: 'Precauções e restrições relevantes' },
  { key: 'patientGoals', label: 'Objetivos relatados pelo paciente' },
  { key: 'professionalSynthesis', label: 'Síntese da avaliação profissional' },
  { key: 'prognosis', label: 'Prognóstico fisioterapêutico' },
  { key: 'initialPlan', label: 'Plano terapêutico' },
  { key: 'followUpPlan', label: 'Seguimento e reavaliação' },
  { key: 'closureReason', label: 'Motivo do encerramento e orientações' },
  { key: 'additionalNotes', label: 'Observações complementares' },
];

const toTrimmedString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const normalizeAnamnesisSections = (value: unknown): AnamnesisSections => {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};

  return {
    chiefComplaint: toTrimmedString(source.chiefComplaint),
    currentIllnessHistory: toTrimmedString(source.currentIllnessHistory),
    personalHistory: toTrimmedString(source.personalHistory),
    surgicalHistory: toTrimmedString(source.surgicalHistory),
    familyHistory: toTrimmedString(source.familyHistory),
    medications: toTrimmedString(source.medications),
    allergies: toTrimmedString(source.allergies),
    habitsAndSocialContext: toTrimmedString(source.habitsAndSocialContext),
    additionalNotes: toTrimmedString(source.additionalNotes),
  };
};

export const normalizeInitialAssessmentAnamnesisSnapshot = (
  value: unknown,
): InitialAssessmentAnamnesisSnapshot | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const source = value as Record<string, unknown>;
  const sourceRecordId = toTrimmedString(source.sourceRecordId);
  const sourceRecordDate = toTrimmedString(source.sourceRecordDate);
  const documentVersion = Number(source.documentVersion);
  const sections = normalizeAnamnesisSections(source.sections);
  const hasContent = Object.values(sections).some(Boolean);

  if (!sourceRecordId || !sourceRecordDate || !Number.isFinite(Date.parse(sourceRecordDate)) || !hasContent) return null;

  return {
    sourceRecordId,
    sourceRecordDate,
    ...(toTrimmedString(source.sourceRecordTitle)
      ? { sourceRecordTitle: toTrimmedString(source.sourceRecordTitle) }
      : {}),
    ...(source.sourceKind === 'structured_anamnesis' ||
    source.sourceKind === 'initial_anamnesis' ||
    source.sourceKind === 'clinical_history_anamnesis'
      ? { sourceKind: source.sourceKind }
      : {}),
    documentVersion: Number.isInteger(documentVersion) && documentVersion > 0 ? documentVersion : 1,
    sections,
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const normalizePhysicalExamination = (value: unknown): InitialAssessmentPhysicalFinding[] => {
  if (!Array.isArray(value)) return [];
  const usedIds = new Set<string>();
  return value.flatMap((item, index) => {
    if (!isRecord(item)) return [];
    const label = toTrimmedString(item.label);
    const finding = toTrimmedString(item.finding);
    if (!label && !finding) return [];
    const baseId = toTrimmedString(item.id) || `finding-${index + 1}`;
    let id = baseId;
    let suffix = index + 1;
    while (usedIds.has(id)) id = `${baseId}-${suffix++}`;
    usedIds.add(id);
    const status: PhysicalExaminationStatus = item.status === 'normal' || item.status === 'altered' || item.status === 'not_applicable'
      ? item.status : 'not_examined';
    return [{ id, domain: toTrimmedString(item.domain), label, status, finding }];
  });
};

const normalizeMeasurableGoals = (value: unknown): InitialAssessmentMeasurableGoal[] => {
  if (!Array.isArray(value)) return [];
  const usedIds = new Set<string>();
  return value.flatMap((item, index) => {
    if (!isRecord(item)) return [];
    const activity = toTrimmedString(item.activity);
    const baseline = toTrimmedString(item.baseline);
    const target = toTrimmedString(item.target);
    if (!activity && !baseline && !target) return [];
    const baseId = toTrimmedString(item.id) || `goal-${index + 1}`;
    let id = baseId;
    let suffix = index + 1;
    while (usedIds.has(id)) id = `${baseId}-${suffix++}`;
    usedIds.add(id);
    const status: MeasurableGoalStatus = item.status === 'achieved' || item.status === 'partial' || item.status === 'not_achieved'
      ? item.status : 'planned';
    return [{ id, activity, baseline, target, deadline: toTrimmedString(item.deadline), status }];
  });
};

export const normalizeInitialAssessmentExternalExams = (value: unknown): InitialAssessmentExternalExamSnapshot[] => {
  if (!Array.isArray(value)) return [];
  const ids = new Set<string>();
  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const recordId = toTrimmedString(item.recordId);
    const recordDate = toTrimmedString(item.recordDate);
    if (!recordId || ids.has(recordId) || !recordDate || !Number.isFinite(Date.parse(recordDate))) return [];
    ids.add(recordId);
    const sourceUpdatedAt = toTrimmedString(item.sourceUpdatedAt);
    return [{ recordId, title: toTrimmedString(item.title) || 'Exame complementar', recordDate, note: toTrimmedString(item.note), sourceUpdatedAt: sourceUpdatedAt && Number.isFinite(Date.parse(sourceUpdatedAt)) ? sourceUpdatedAt : null }];
  });
};

export const normalizeInitialAssessmentPayload = (
  value: unknown,
): NormalizedInitialAssessmentPayload | null => {
  if (!isRecord(value)) return null;
  const source = value;
  const assessmentReason = toTrimmedString(source.assessmentReason);
  const professionalSynthesis = toTrimmedString(source.professionalSynthesis);
  const anamnesisSnapshot = normalizeInitialAssessmentAnamnesisSnapshot(source.anamnesisSnapshot);
  const physicalExamination = normalizePhysicalExamination(source.physicalExamination);
  const measurableGoals = normalizeMeasurableGoals(source.measurableGoals);
  const externalExams = normalizeInitialAssessmentExternalExams(source.externalExams);
  const selectedIds = new Set<string>();
  const selectedResults = (Array.isArray(source.selectedResults) ? source.selectedResults : [])
    .map(normalizeAssessmentReportSourceSnapshot)
    .filter((result): result is AssessmentReportSourceSnapshotV2 => {
      if (!result || !result.resultId || !result.snapshot.testKey || !Number.isFinite(Date.parse(result.snapshot.assessmentDate)) || selectedIds.has(result.resultId) || result.snapshot.sourceStatus !== 'completed'
        || ['INITIAL_ASSESSMENT', 'ASSESSMENT_REPORT', 'COMPOSITE_REPORT'].includes(result.snapshot.testKey)) return false;
      selectedIds.add(result.resultId);
      return true;
    });
  const hasText = INITIAL_ASSESSMENT_FIELD_LABELS.some(({ key }) => Boolean(toTrimmedString(source[key])))
    || Boolean(toTrimmedString(source.episodeLabel));
  if (!hasText && !anamnesisSnapshot && !physicalExamination.length && !measurableGoals.length && !selectedResults.length && !externalExams.length) return null;

  return {
    schemaVersion: INITIAL_ASSESSMENT_SCHEMA_VERSION,
    assessmentReason,
    referralDiagnosis: toTrimmedString(source.referralDiagnosis),
    currentClinicalCondition: toTrimmedString(source.currentClinicalCondition),
    mainSymptoms: toTrimmedString(source.mainSymptoms),
    functionalLimitations: toTrimmedString(source.functionalLimitations),
    assistiveDevices: toTrimmedString(source.assistiveDevices),
    precautions: toTrimmedString(source.precautions),
    patientGoals: toTrimmedString(source.patientGoals),
    professionalSynthesis,
    initialPlan: toTrimmedString(source.initialPlan),
    additionalNotes: toTrimmedString(source.additionalNotes),
    assessmentMoment: source.assessmentMoment === 'reassessment' || source.assessmentMoment === 'final' ? source.assessmentMoment : 'initial',
    episodeLabel: toTrimmedString(source.episodeLabel),
    baselineResultId: toTrimmedString(source.baselineResultId) || null,
    previousFunction: toTrimmedString(source.previousFunction),
    physicalExamination,
    measurableGoals,
    prognosis: toTrimmedString(source.prognosis),
    followUpPlan: toTrimmedString(source.followUpPlan),
    closureReason: toTrimmedString(source.closureReason),
    selectedResults,
    externalExams,
    anamnesisSnapshot,
    finishedAt: toTrimmedString(source.finishedAt) && Number.isFinite(Date.parse(toTrimmedString(source.finishedAt)))
      ? toTrimmedString(source.finishedAt) : new Date().toISOString(),
    ...(isRecord(source.patientContext) ? { patientContext: source.patientContext } : {}),
    ...(isRecord(source.professionalContext) ? { professionalContext: source.professionalContext } : {}),
  };
};

export const getInitialAssessmentMomentLabel = (value: unknown): string => {
  const normalized = normalizeInitialAssessmentPayload(value);
  return INITIAL_ASSESSMENT_MOMENT_LABELS[normalized?.assessmentMoment ?? 'initial'];
};

export const formatInitialAssessmentDate = (value?: string | null): string => {
  if (!value) return 'Data não informada';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Data não informada' : date.toLocaleString('pt-BR');
};

export const isInitialAssessmentComplete = (value: unknown): boolean => {
  const payload = normalizeInitialAssessmentPayload(value);
  return Boolean(payload?.assessmentReason && payload.professionalSynthesis);
};

export const summarizeInitialAssessment = (value: unknown): InitialAssessmentSummary => {
  const payload = normalizeInitialAssessmentPayload(value);
  const filledSections = payload
    ? INITIAL_ASSESSMENT_FIELD_LABELS.filter(({ key }) => Boolean(payload[key])).length
    : 0;

  return {
    statusLabel: payload?.assessmentMoment === 'reassessment'
      ? (isInitialAssessmentComplete(payload) ? 'Reavaliação concluída' : 'Reavaliação em andamento')
      : payload?.assessmentMoment === 'final'
        ? (isInitialAssessmentComplete(payload) ? 'Avaliação final concluída' : 'Avaliação final em andamento')
        : (isInitialAssessmentComplete(payload) ? 'Ficha concluída' : 'Ficha em andamento'),
    classification: 'Registro clínico descritivo',
    filledSections,
    hasAnamnesisSnapshot: Boolean(payload?.anamnesisSnapshot),
    assessmentReason: payload?.assessmentReason ?? '',
    professionalSynthesis: payload?.professionalSynthesis ?? '',
  };
};

export interface InitialAssessmentSourceIssue {
  resultId: string;
  reason: 'unavailable' | 'different_patient' | 'different_client' | 'changed';
}

/** Checks the identity and revision of the selected evidence without replacing its saved snapshot. */
export const getInitialAssessmentSourceIssues = (
  selectedResults: readonly AssessmentReportSourceSnapshotV2[],
  results: readonly AssessmentResultV2[],
  identity: { clientId: string; patientId: string },
): InitialAssessmentSourceIssue[] => selectedResults.flatMap<InitialAssessmentSourceIssue>((source) => {
  const current = results.find((result) => result.resultId === source.resultId);
  if (!current || current.status !== 'completed') return [{ resultId: source.resultId, reason: 'unavailable' as const }];
  if (current.clientId !== identity.clientId) return [{ resultId: source.resultId, reason: 'different_client' as const }];
  if (current.patientId !== identity.patientId) return [{ resultId: source.resultId, reason: 'different_patient' as const }];
  const expected = source.snapshot.sourceUpdatedAt || source.snapshot.sourceCreatedAt;
  const actual = current.updatedAt || current.createdAt;
  if (!expected || !actual || !Number.isFinite(Date.parse(expected)) || Date.parse(expected) !== Date.parse(actual)
    || source.sessionId !== current.sessionId
    || source.snapshot.testKey !== current.testKey || source.snapshot.testVersion !== current.testVersion
    || Date.parse(source.snapshot.assessmentDate) !== Date.parse(current.assessmentDate)) {
    return [{ resultId: source.resultId, reason: 'changed' as const }];
  }
  return [];
});
